import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { getAnalytics, logEvent, setAnalyticsCollectionEnabled } from 'firebase/analytics';
import { firebaseApp } from '../firebase/config';
import {
  isAnalyticsOptedOut,
  saveAnalyticsOptOut,
  clearAnalyticsCookies
} from '../firebase/analyticsPreference';

const FirebaseAnalyticsContext = createContext(null);

export const useAnalytics = () => {
  return useContext(FirebaseAnalyticsContext);
};

// The Analytics instance, or null where the browser can't run it (some in-app
// browsers) - analytics must never take the app down with it.
// setAnalyticsCollectionEnabled(true) undoes an opt-out made earlier in the
// same page load, which leaves gtag's window['ga-disable-<id>'] flag set.
const startAnalytics = () => {
  try {
    const analytics = getAnalytics(firebaseApp);
    setAnalyticsCollectionEnabled(analytics, true);
    return analytics;
  } catch (error) {
    console.warn('Analytics unavailable:', error);
    return null;
  }
};

export const FirebaseAnalyticsProvider = ({ children }) => {
  // A lazy initializer rather than an effect: children's effects (RouteTracker's
  // first page_view) run before this provider's own effects would, so the
  // instance has to exist by the end of the first render.
  const [analyticsEnabled, setAnalyticsEnabledState] = useState(() => !isAnalyticsOptedOut());
  const [analytics, setAnalytics] = useState(() => (isAnalyticsOptedOut() ? null : startAnalytics()));

  // The Privacy Policy page's switch. Turning analytics off stops collection
  // immediately, clears gtag's cookies, and is remembered so gtag.js isn't
  // even loaded on this device's next visit.
  const setAnalyticsEnabled = useCallback((enabled) => {
    saveAnalyticsOptOut(!enabled);
    setAnalyticsEnabledState(enabled);

    if (enabled) {
      setAnalytics(startAnalytics());
      return;
    }

    if (analytics) {
      try {
        setAnalyticsCollectionEnabled(analytics, false);
      } catch (error) {
        console.warn('Could not disable analytics collection:', error);
      }
    }
    clearAnalyticsCookies();
    setAnalytics(null);
  }, [analytics]);

  // Analytics helper functions - no-ops while analytics is off or unavailable
  const trackEvent = useCallback((eventName, eventParams = {}) => {
    if (analytics) {
      logEvent(analytics, eventName, eventParams);
    }
  }, [analytics]);

  const trackPageView = useCallback((pagePath, pageTitle = document.title) => {
    if (analytics) {
      logEvent(analytics, 'page_view', {
        page_path: pagePath,
        page_title: pageTitle
      });
    }
  }, [analytics]);

  const value = useMemo(() => ({
    analytics,
    analyticsEnabled,
    setAnalyticsEnabled,
    trackEvent,
    trackPageView
  }), [analytics, analyticsEnabled, setAnalyticsEnabled, trackEvent, trackPageView]);

  return (
    <FirebaseAnalyticsContext.Provider value={value}>
      {children}
    </FirebaseAnalyticsContext.Provider>
  );
};
